import { useEffect, useState, useRef } from "react";
import { message } from "antd";
import Papa from "papaparse";
import axiosInstance from "../../../../utils/axiosInstance";
import { GeoLiftJobConfigService } from "../../../../services/jobConfigServiceFactory/GeoLiftJobConfigService";
import { getTenantId } from "../../../../utils/auth";
import { FormInstance } from "antd/lib/form";

// Types
export interface AggregateResponse {
  lift: {
    totalLiftPercentage: number;
    totalSalesWithCampaign: number;
    totalSalesWithoutCampaign: number;
  };
  roas: {
    totalROAS: number;
    totalAdSpendForSyntheticMarkets: number;
    totalIncrementSales: number;
  };
  graph: {
    totalLiftPercentage: number;
    totalSalesWithCampaign: number;
    totalSalesWithoutCampaign: number;
    salesWithCampaignData: number[];
    salesWithoutCampaignData: number[];
    labels?: any;
  };
  isSales: boolean;
}

export interface GeoLiftResponse {
  lift_plot: {
    t_obs: { [key: number]: number };
    c_obs: { [key: number]: number };
    Time: { [key: number]: number };
    date: { [key: number]: number };
    startCampaignDate: { [key: number]: number };
    startCampaignIndex: { [key: number]: number };
  };
  att_plot: {
    Time: { [key: number]: number };
    Estimate: { [key: number]: number };
  };
  salesWithoutCampaign: number;
  pvalue: number;
  salesWithCampaign: number;
  average_att: number;
  lift_percentage: number;
  incremental: number;
  start_duration: number;
  end_duration: number;
  isSales: boolean;
}

export type ResponseData = GeoLiftResponse | AggregateResponse;

export interface MarketResult {
  marketName: string;
  status: "pending" | "processing" | "completed" | "error";
  data: any;
  error?: string;
  index: number;
}

export interface GeoLiftDataProcessorProps {
  jobConfigId?: string;
  form: FormInstance;
  testMarkets: string[];
  currentSyntheticMarket: string;
  handleTestMarketChange: (market: string) => void;
  mode: any;
}

export interface GeoLiftDataProcessorResult {
  file: File | null;
  marketResults: MarketResult[];
  batchProcessing: boolean;
  currentProcessingIndex: number;
  aggregateData: AggregateResponse | null;
  aggregatedLocationsData: GeoLiftResponse | null;
  processAllMarkets: () => Promise<void>;
  fetchAggregatedLocationsData: () => Promise<ResponseData | null>;
  isGeoLiftResponse: (
    data: ResponseData | null | undefined
  ) => data is GeoLiftResponse;
  isAggregateResponse: (
    data: ResponseData | null | undefined
  ) => data is AggregateResponse;
  formatMarketName: (marketName: string) => string;
}

// Type guard functions
export const isGeoLiftResponse = (
  data: ResponseData | null | undefined
): data is GeoLiftResponse => {
  return (
    !!data &&
    typeof data === "object" &&
    "lift_plot" in data &&
    "salesWithoutCampaign" in data
  );
};

export const isAggregateResponse = (
  data: ResponseData | null | undefined
): data is AggregateResponse => {
  return (
    !!data &&
    typeof data === "object" &&
    "lift" in data &&
    "roas" in data &&
    "graph" in data
  );
};

// Format market name function
export const formatMarketName = (marketName: string) => {
  if (!marketName) return "";
  // Split by comma and take only the first part
  const name = marketName.split(",")[0];
  // Remove any parentheses and single quotes
  const cleanName = name.replace(/[()']/g, "");
  // Capitalize first letter of each word
  return cleanName
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ")
    .trim();
};

export const useGeoLiftDataProcessor = ({
  jobConfigId,
  form,
  testMarkets,
  currentSyntheticMarket,
  handleTestMarketChange,
  mode,
}: GeoLiftDataProcessorProps): GeoLiftDataProcessorResult => {
  const [marketResults, setMarketResults] = useState<MarketResult[]>([]);
  const [batchProcessing, setBatchProcessing] = useState<boolean>(false);
  const [file, setFile] = useState<File | null>(null);
  const [currentProcessingIndex, setCurrentProcessingIndex] =
    useState<number>(-1);
  const [aggregateData, setAggregateData] = useState<AggregateResponse | null>(
    null
  );
  const [aggregatedLocationsData, setAggregatedLocationsData] =
    useState<GeoLiftResponse | null>(null);
  const hasShownSuccessMessage = useRef<boolean>(false);

  const geoLiftJobConfigService = new GeoLiftJobConfigService();

  // Function to get file from S3
  const getFileHandler = async () => {
    try {
      const fileResponse = await geoLiftJobConfigService.getFile(
        `${jobConfigId}/input/geo_lift_post_data.csv`
      );

      if (!fileResponse) {
        console.error("No post-test data file found in S3");
        return;
      }
      const blob = new Blob([fileResponse], { type: "text/csv" });
      const csvFile = new File([blob], "geo_lift_post_data.csv", {
        type: "text/csv",
      });
      console.log(csvFile);
      setFile(csvFile);
    } catch (error) {
      console.error("Error fetching file from S3:", error);
    }
  };

  // Initialize file when jobConfigId changes
  useEffect(() => {
    if (jobConfigId) {
      getFileHandler();
    }
  }, [jobConfigId]);

  // Initialize market results when test markets change
  useEffect(() => {
    if (testMarkets.length > 0) {
      setMarketResults(
        testMarkets
          .filter((market) => market !== "Aggregated Locations")
          .map((market, index) => ({
            marketName: market,
            status: "pending",
            data: null,
            index: index + 1,
          }))
      );
    }
  }, [testMarkets]);

  // Auto-process markets when file and test markets are available
  useEffect(() => {
    if (file && testMarkets.length > 0 && jobConfigId) {
      processAllMarkets();
    }
  }, [testMarkets, file]);

  // Fetch Aggregated Locations data when component mounts
  useEffect(() => {
    if (
      jobConfigId &&
      file &&
      testMarkets.includes("Aggregated Locations")
      // &&
      // !aggregatedLocationsData
    ) {
      fetchAggregatedLocationsData();
    }
  }, [jobConfigId, testMarkets, file]);
  // Function to process all markets in batch
  const processAllMarkets = async () => {
    if (!file) {
      await getFileHandler();
      // Wait a bit for the file to be set in state
      await new Promise((resolve) => setTimeout(resolve, 2200));
      if (!file) {
        message.error("No post-test data file found");
        return;
      }
    }

    setBatchProcessing(true);

    try {
      // Process each market one by one
      for (let i = 0; i < marketResults.length; i++) {
        const market = marketResults[i];
        setCurrentProcessingIndex(i);

        // Update status to processing
        setMarketResults((prev) =>
          prev.map((m) =>
            m.marketName === market.marketName
              ? { ...m, status: "processing" }
              : m
          )
        );

        try {
          // Set the current market in the form
          form.setFieldsValue({ testMarket: market.marketName });

          // Trigger the market change to update synthetic options
          handleTestMarketChange(market.marketName);

          // Wait for synthetic market options to update
          await new Promise((resolve) => setTimeout(resolve, 500));

          // Get form values
          const values = form.getFieldsValue();
          const formData = new FormData();

          formData.append("csv_file", file);

          // Calculate ad spend for this market
          let marketAdSpend = 0;
          const adSpendColumn = "adspend";

          // Parse the file to get ad spend for this market
          await new Promise<void>((resolve, reject) => {
            Papa.parse(file, {
              complete: (results: any) => {
                marketAdSpend = results.data
                  .filter((row: any) => {
                    // Adjust this filter based on how markets are identified in your CSV
                    return (
                      row.Market === market.marketName ||
                      row.Location === market.marketName
                    );
                  })
                  .reduce((sum: number, row: any) => {
                    const value = parseFloat(row[adSpendColumn]);
                    return isNaN(value) ? sum : sum + value;
                  }, 0);

                resolve();
              },
              error: (error) => {
                console.error("Error parsing CSV:", error);
                reject(error);
              },
              header: true,
              skipEmptyLines: true,
              dynamicTyping: true,
            });
          });

          // For individual market
          formData.append("marketSelectionId", "0");
          formData.append("startDuration", "97");
          formData.append("endDuration", "105");
          const syntheticMarketValue =
            values.syntheticMarket || currentSyntheticMarket || "";
          formData.append("marketsList", syntheticMarketValue);
          formData.append("jobConfigId", jobConfigId || "");
          formData.append("tenantId", getTenantId());
          formData.append("market_name", market.marketName);
          formData.append("adSpendColumn", "adspend");
          formData.append("calculatedAdSpend", marketAdSpend.toString());

          const response = await axiosInstance.post(
            "/model/geo_lift/post_test",
            formData
          );

          if (response.data) {
            // Update market results with the data
            setMarketResults((prev) =>
              prev.map((m) =>
                m.marketName === market.marketName
                  ? { ...m, status: "completed", data: response.data }
                  : m
              )
            );
          }
        } catch (error) {
          console.error(`Error processing market ${market.marketName}:`, error);
        }
      }

      // Show success message only once
      if (!hasShownSuccessMessage.current) {
        message.success("All markets processed successfully!");
        hasShownSuccessMessage.current = true;
      }
    } catch (error) {
      console.error("Error in batch processing:", error);
      message.error("An error occurred during batch processing");
    } finally {
      setBatchProcessing(false);
      setCurrentProcessingIndex(-1);
    }
  };

  // Function to fetch Aggregated Locations data
  const fetchAggregatedLocationsData = async () => {
    try {
      form.setFieldsValue({ testMarket: "Aggregated Locations" });

      // Trigger the market change to update synthetic options
      handleTestMarketChange("Aggregated Locations");

      // Wait for synthetic market options to update
      await new Promise((resolve) => setTimeout(resolve, 500));

      const formData = new FormData();

      // Check if file exists
      if (!file) {
        await getFileHandler();
        // Wait a bit for the file to be set in state
        await new Promise((resolve) => setTimeout(resolve, 300));
        if (!file) {
          throw new Error("No post-test data file found");
        }
      }

      formData.append("csv_file", file);

      let totalAdSpend = 0;
      const adSpendColumn = "adspend";

      // Parse the file to get total ad spend (if column exists)
      await new Promise<void>((resolve, reject) => {
        Papa.parse(file, {
          complete: (results: any) => {
            const fields = results.meta.fields || [];

            if (!fields.includes(adSpendColumn)) {
              console.warn(
                `Column "${adSpendColumn}" not found in CSV. Defaulting ad spend to 0.`
              );
              // Continue without rejecting
              resolve();
              return;
            }

            totalAdSpend = results.data.reduce((sum: number, row: any) => {
              const value = parseFloat(row[adSpendColumn]);
              return isNaN(value) ? sum : sum + value;
            }, 0);

            console.log("Calculated total ad spend:", totalAdSpend);
            resolve();
          },
          error: (error) => {
            console.error("Error parsing CSV:", error);
            reject(error);
          },
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
        });
      });

      // Append form data for API call
      formData.append("jobConfigId", jobConfigId || "");
      formData.append("tenantId", getTenantId());
      formData.append("confidenceThreshold", "80");
      // formData.append("startDuration", "97");
      // formData.append("endDuration", "105");
      formData.append("adSpendColumn", adSpendColumn); // Still append the name
      formData.append("totalAdSpend", totalAdSpend.toString());
      formData.append("adsSpent", "0");
      // Use the correct endpoint from useGeoLiftForm for Aggregated Locations
      const endpoint = "/model/geo_lift/aggregate_lift_percentage";
      console.log("Sending request to:", endpoint);
      console.log("FormData contents:", Object.fromEntries(formData.entries()));

      const response = await axiosInstance.post(endpoint, formData);

      if (response.data) {
        console.log("API response:", response.data);
        // Store the Aggregated Locations data
        setAggregatedLocationsData(response.data);
        setAggregateData(response.data);
        return response.data;

      } else {
        console.error(
          "Invalid Aggregated Locations data format:",
          response.data
        );
        return false;
      }
    } catch (error) {
      return false;
    }
  };

  return {
    file,
    marketResults,
    batchProcessing,
    currentProcessingIndex,
    aggregateData,
    aggregatedLocationsData,
    processAllMarkets,
    fetchAggregatedLocationsData,
    isGeoLiftResponse,
    isAggregateResponse,
    formatMarketName,
  };
};

export default useGeoLiftDataProcessor;
